import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import cx from 'classnames';
import moment from 'moment';
import find from 'ramda/src/find';
import includes from 'ramda/src/includes';
import isNil from 'ramda/src/isNil';
import propEq from 'ramda/src/propEq';
import Avatar from '@components/@core/Avatar';
import CheckBox from '@components/@core/CheckBox';
import Icon from '@components/@core/Icon';
import EditDropdown from '@components/EditDropdown';
import AppActions from '~/actions/app';
import ManageActions from '~/actions/manage';
import { RecurringType, TaskDotsType, UserRoles } from '~/services/config';
import { Habit_Type, Unknown_User } from '~/services/constants';

const TaskItem = ({ className = '', task = {}, after = null, completed = false }) => {
    const dispatch = useDispatch();
    const users = useSelector((state) => state.app.users);
    const role = useSelector((state) => state.app.app_role_id);
    const userIds = useSelector((state) => state.app.selectedEmployee.users);
    const projects = useSelector((state) => state.app.projects);
    const [checked, setChecked] = useState(!!task.completed_at);
    const user = useMemo(() => {
        return find(propEq('id', task.user_id), users) || Unknown_User;
    }, [users, task]);
    const project = useMemo(() => {
        return find(propEq('id', task.project_id), projects) || {};
    }, [projects]);

    const handleCheck = (e) => {
        if (completed) return;

        if (role > UserRoles.MANAGER && !includes(task.user_id, userIds)) {
            toast.error('You do not have permission to do that.', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
            return;
        }
        if (role > UserRoles.MANAGER && project.name === 'Approval') {
            toast.error(
                'Your Manager must complete this Approval Task in their feed. This copy of the task is a reminder that you sent them a request for approval. It will auto-complete once your manager gives approval.',
                {
                    position: toast.POSITION.TOP_RIGHT,
                    pauseOnFocusLoss: false,
                    hideProgressBar: true,
                }
            );
            return;
        }
        if (e.target.checked && !checked) {
            dispatch(ManageActions.updatetaskRequest('completed', task.id));
        } else {
            dispatch(ManageActions.updatetaskRequest('incompleted', task.id));
        }
        setChecked(e.target.checked);
    };

    const handleModal = (e) => {
        let payload = {};
        switch (e) {
            case 'archive': {
                if (role > UserRoles.MANAGER && !includes(task.user_id, userIds)) {
                    toast.error('You do not have permission to do that.', {
                        position: toast.POSITION.TOP_RIGHT,
                        pauseOnFocusLoss: false,
                        hideProgressBar: true,
                    });
                    return;
                }
                payload = {
                    type: 'Confirm',
                    data: {
                        before: {
                            title: 'Archive',
                            body: 'Are you sure you want to archive this item ?',
                        },
                    },
                    callBack: {
                        onYes: () => dispatch(ManageActions.updatetaskRequest('archived', task.id)),
                    },
                };
                break;
            }
            case 'delete': {
                if (role > UserRoles.MANAGER && !includes(task.user_id, userIds)) {
                    toast.error('You do not have permission to do that.', {
                        position: toast.POSITION.TOP_RIGHT,
                        pauseOnFocusLoss: false,
                        hideProgressBar: true,
                    });
                    return;
                }
                payload = {
                    type: 'Confirm',
                    data: {
                        before: {
                            title: 'Delete',
                            body: 'Are you sure you want to delete this item?',
                        },
                    },
                    callBack: {
                        onYes: () => dispatch(ManageActions.deletetaskRequest(task.id, 'task')),
                    },
                };
                break;
            }
            case 'edit': {
                const self = isNil(task.assigned_by) || includes(task.assigned_by, userIds);
                const _after = after || {
                    type: 'GETTASKSFEED_REQUEST',
                    userId: task.user_id,
                    payload: { page: 1, perPage: 500 },
                };
                if (role > UserRoles.MANAGER && !self) {
                    toast.error('You do not have permission to do that.', {
                        position: toast.POSITION.TOP_RIGHT,
                        pauseOnFocusLoss: false,
                        hideProgressBar: true,
                    });
                    return;
                }
                payload = { type: 'Edit Task', data: { before: { card: task, after: _after } } };
                break;
            }
            case 'detail view': {
                payload = { type: 'Task Detail', data: { before: { card: task, after } } };
                break;
            }
            default:
                break;
        }
        dispatch(AppActions.modalRequest(payload));
    };

    const isTask = useMemo(() => task.data.type === 'single', [task]);
    const type = useMemo(() => {
        const getType = () => {
            return task.data.schedule_interval === 'month' ? Habit_Type.month : task.data.schedule_interval === 'week' ? Habit_Type.week : Habit_Type.day;
        };
        let temp = task.project_id ? project?.name || 'General' : getType();
        if (isTask) {
            temp = `${temp} (Requested by ${task.data.user_name || '...'})`;
        } else if (!isNil(task.data.approval_id)) {
            temp = RecurringType[task.data.schedule_interval || 'month'].label;
        }
        return temp;
    }, [task, isTask]);

    return (
        <div className={cx('core-task-item', className)}>
            <CheckBox id={task.id} size="regular" checked={checked} onChange={handleCheck} />
            <div className="task-detail" onClick={() => handleModal('detail view')}>
                <div className="d-flex mb-1">
                    <span className="dsl-m12 text-400">{type}</span>
                    {task.data.comments && <Icon name="fas fa-comment ms-2" size={14} active={!includes(task.data.comments_viewed[0], userIds)} />}
                </div>
                <p className={cx('dsl-b14 mb-0 text-400', checked && ' text-line-through')}>
                    {task.data.quota_actual_task === 'yes' ? `Save Actuals for ${moment(task.created_at).subtract(1, 'month').format('MMMM')}` : task.data.name}
                </p>
            </div>
            <div className="avatar-section">
                <Avatar name={user.name} url={user.avatar || user.community_user?.avatar} />
                <div className="d-flex mt-1">
                    {isTask && task.status === 2 && <Icon name="fas fa-exclamation-triangle me-1" color="#ff0000" size={10} />}
                    <span className={isTask && task.status === 2 ? 'dsl-r12' : 'dsl-b12'}>
                        {isNil(task.due_at) ? moment.unix(task.data.due_date).format('M/D/YY') : moment(task.due_at).format('M/D/YY')}
                    </span>
                </div>
            </div>
            <EditDropdown options={TaskDotsType[role]} onChange={handleModal} />
        </div>
    );
};

export default TaskItem;
